import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import { useBusinessAccountContext } from '@/hooks/useBusinessAccountContext'
import { useChat } from '@/contexts/ChatContext'

interface MessageStatus {
  status: 'SENT' | 'DELIVERED' | 'READ' | 'FAILED'
  delivered_at?: string | null
  read_at?: string | null
  error_message?: string | null
  updated_at?: string
}

interface MessageStatuses {
  byMessageId: Record<string, MessageStatus>
  byWhatsappMessageId: Record<string, MessageStatus>
}

interface MessageStatusContextType {
  getMessageStatus: (messageId: string, whatsappMessageId?: string | null) => MessageStatus | null
  updateMessageStatus: (messageId: string, status: MessageStatus, whatsappMessageId?: string | null) => void
  clearStatuses: () => void
}

const MessageStatusContext = createContext<MessageStatusContextType | undefined>(undefined)

export function MessageStatusProvider({ children }: { children: React.ReactNode }) {
  const businessAccountContext = useBusinessAccountContext()
  const chatContext = useChat()
  const [statuses, setStatuses] = useState<MessageStatuses>({
    byMessageId: {},
    byWhatsappMessageId: {}
  })
  const processedEvents = useRef<Set<string>>(new Set())

  const socket = chatContext?.socket ?? null
  const hasProviders = Boolean(businessAccountContext && chatContext)

  useEffect(() => {
    if (!socket) {
      return
    }

    const handleMessageStatusUpdate = (data: any) => {
      const conversationId = data.conversationId || data.conversation_id
      const messageId = data.messageId || data.message_id
      const whatsappMessageId = data.whatsappMessageId || data.whatsapp_message_id
      const status = (data.status || '').toUpperCase() as MessageStatus['status']

      if (!messageId && !whatsappMessageId) {
        return
      }

      if (!status || !['SENT', 'DELIVERED', 'READ', 'FAILED'].includes(status)) {
        return
      }

      const eventKey = `${status}-${conversationId}-${messageId || whatsappMessageId}-${data.timestamp || Date.now()}`
      if (processedEvents.current.has(eventKey)) {
        return
      }
      processedEvents.current.add(eventKey)

      const incomingError = data.error_message || data.errorMessage || null

      setStatuses(prev => {
        const RANK: Record<string, number> = { SENT: 1, DELIVERED: 2, READ: 3 }
        const existing =
          (messageId ? prev.byMessageId[messageId] : undefined) ||
          (whatsappMessageId ? prev.byWhatsappMessageId[whatsappMessageId] : undefined)
        if (
          status !== 'FAILED' &&
          existing?.status &&
          existing.status !== 'FAILED' &&
          (RANK[status] ?? 0) <= (RANK[existing.status] ?? 0)
        ) {
          return prev
        }

        const newStatus: MessageStatus = {
          status,
          delivered_at: status === 'DELIVERED'
            ? (data.timestamp || data.deliveredAt || data.delivered_at || new Date().toISOString())
            : prev.byMessageId[messageId]?.delivered_at || prev.byWhatsappMessageId[whatsappMessageId || '']?.delivered_at || null,
          read_at: status === 'READ'
            ? (data.timestamp || data.readAt || data.read_at || new Date().toISOString())
            : prev.byMessageId[messageId]?.read_at || prev.byWhatsappMessageId[whatsappMessageId || '']?.read_at || null,
          error_message: status === 'FAILED'
            ? (incomingError || prev.byMessageId[messageId]?.error_message || prev.byWhatsappMessageId[whatsappMessageId || '']?.error_message || null)
            : null,
          updated_at: data.timestamp || new Date().toISOString()
        }

        const updated = {
          ...prev,
          byMessageId: { ...prev.byMessageId },
          byWhatsappMessageId: { ...prev.byWhatsappMessageId },
        }

        if (messageId) {
          updated.byMessageId[messageId] = {
            ...updated.byMessageId[messageId],
            ...newStatus
          }
        }

        if (whatsappMessageId) {
          updated.byWhatsappMessageId[whatsappMessageId] = {
            ...updated.byWhatsappMessageId[whatsappMessageId],
            ...newStatus
          }
        }

        return updated
      })

      setTimeout(() => processedEvents.current.delete(eventKey), 5000)
    }

    socket.on('message_status_update', handleMessageStatusUpdate)

  }, [socket]) // Only depend on socket, not isConnected - attach when socket exists

  const getMessageStatus = useCallback((messageId: string, whatsappMessageId?: string | null): MessageStatus | null => {
    if (statuses.byMessageId[messageId]) {
      return statuses.byMessageId[messageId]
    }
    
    if (whatsappMessageId && statuses.byWhatsappMessageId[whatsappMessageId]) {
      return statuses.byWhatsappMessageId[whatsappMessageId]
    }

    return null
  }, [statuses])

  const updateMessageStatus = useCallback((messageId: string, status: MessageStatus, whatsappMessageId?: string | null) => {
    setStatuses(prev => {
      const updated = { ...prev }
      
      if (messageId) {
        updated.byMessageId[messageId] = status
      }

      if (whatsappMessageId) {
        updated.byWhatsappMessageId[whatsappMessageId] = status
      }

      return updated
    })
  }, [])

  const clearStatuses = useCallback(() => {
    setStatuses({
      byMessageId: {},
      byWhatsappMessageId: {}
    })
  }, [])

  const value: MessageStatusContextType = hasProviders
    ? { getMessageStatus, updateMessageStatus, clearStatuses }
    : { getMessageStatus: () => null, updateMessageStatus: () => {}, clearStatuses: () => {} }

  return (
    <MessageStatusContext.Provider value={value}>
      {children}
    </MessageStatusContext.Provider>
  )
}

export function useMessageStatus() {
  const context = useContext(MessageStatusContext)
  if (context === undefined) {
    return {
      getMessageStatus: () => null,
      updateMessageStatus: () => {},
      clearStatuses: () => {}
    }
  }
  return context
}

